import { useState } from 'react';
import { THEME_GROUPS, groupIdForTheme } from '../theme.ts';

/**
 * The theme picker.
 *
 * One flat row of swatches stopped working somewhere around the eighth theme,
 * so the list is grouped and each group collapses. The group holding the
 * current theme starts open — on arriving, the useful thing to see is what is
 * on and what is next to it, not fifteen closed drawers.
 */
export function ThemePicker({
  theme,
  onChange,
}: {
  theme: string;
  onChange: (theme: string) => void;
}) {
  const [openGroup, setOpenGroup] = useState<string | null>(() => groupIdForTheme(theme));

  return (
    <div className="mt-2 divide-y divide-border overflow-hidden rounded-lg border border-border">
      {THEME_GROUPS.map((group) => {
        const open = openGroup === group.id;
        const activeHere = group.themes.some((option) => option.id === theme);

        return (
          <section key={group.id}>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpenGroup(open ? null : group.id)}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-surface-raised"
            >
              <span className="flex shrink-0 -space-x-1" aria-hidden>
                {group.themes.map((option) => (
                  <span
                    key={option.id}
                    className="size-3.5 rounded-full border border-border-strong"
                    style={{ background: option.swatch }}
                  />
                ))}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">
                  {group.label}
                  {activeHere && <span className="ml-2 text-xs text-accent">in use</span>}
                </span>
                <span className="block truncate text-xs text-text-faint">{group.hint}</span>
              </span>
              <span aria-hidden className="shrink-0 text-xs text-text-faint">
                {open ? '−' : '+'}
              </span>
            </button>

            {open && (
              <div className="flex flex-wrap gap-2 px-3 pb-3">
                {group.themes.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => onChange(option.id)}
                    aria-pressed={theme === option.id}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      theme === option.id
                        ? 'border-accent bg-accent/15 text-text'
                        : 'border-border text-text-muted hover:border-border-strong'
                    }`}
                  >
                    <span
                      aria-hidden
                      className="size-3.5 rounded-full border border-border-strong"
                      style={{ background: option.swatch }}
                    />
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
